import {
  AlertIcon,
  Button,
  CheckmarkIcon,
  colors,
  H2,
  Popup,
  PopupHeader,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  XIcon,
} from "@commonsku/styles";
import React, { useRef, useMemo, useState } from "react";
import styled from "styled-components";
import { BASE_ZINDEX } from "../../../../popup-factory";
import { useDispatch } from "react-redux";
import useContainerScroll, {
  ScrollContainer,
} from "../../../../hooks/useContainerScroll";
import { Checkout } from "../../../../interfaces/Checkout";
import {
  getFailedCheckoutsForShop,
  loadShopOrders,
} from "../../../../actions/shop";
import { getCheckoutItemIds } from "../../helpers";
import _ from "lodash";
import MapCheckoutProductsPopup from "../MapCheckoutProductsPopup/MapCheckoutProductsPopup";

export interface MapErrorProductType {
  external_variant_id: string;
  product: string;
  color: string;
  size: string;
  sku: string;
  errorType: string;
  checkoutItemIds: string[];
}

const ContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const BodyContainer = styled.div`
  width: 100%;
  max-height: 376px !important;
  overflow: hidden;
  flex: 1;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

interface CheckoutsErrorPopupProps {
  importedCheckouts: Checkout[];
  shop_id: string;
  order_id: string;
  shop_number: number;
  external_store_type: string;
  onClose?: () => void;
}

const CheckoutsErrorPopup = ({
  importedCheckouts,
  shop_id,
  order_id,
  shop_number,
  external_store_type,
  onClose,
}: CheckoutsErrorPopupProps) => {
  const tableRef = useRef(null);
  const dispatch = useDispatch();
  const onPopupClose = () => {
    dispatch(loadShopOrders(shop_id));
    dispatch(getFailedCheckoutsForShop(shop_id, { has_error: true }));
    onClose?.();
  };
  const { canScrollDown, scrollDown } = useContainerScroll(tableRef);
  const [linkedItemIds, setLinkedItemIds] = useState<string[]>([]);
  const [shopPopup, setShopPopup] = useState<{
    type: string;
    props: any;
  } | null>(null);

  const errorItems = useMemo(() => {
    const items: MapErrorProductType[] = [];

    importedCheckouts.forEach((checkout) => {
      if (!checkout.is_valid) {
        checkout.items.forEach((item) => {
          if (item.error_message) {
            const productAlreadyExists = items.some(
              (existingItem) =>
                existingItem.external_variant_id.toString() ===
                item.external_variant_id.toString()
            );
            if (item.external_variant_id && !productAlreadyExists) {
              items.push({
                external_variant_id: item.external_variant_id.toString(),
                product: item.item_name?.toString() || "",
                color: item.color_name?.toString() || "",
                size: item.size_name?.toString() || "",
                sku: item.external_id?.toString() || "",
                errorType: item.error_message,
                checkoutItemIds:
                  getCheckoutItemIds(
                    importedCheckouts,
                    item.external_variant_id
                  ) || [],
              });
            }
          }
        });
      }
    });

    return items;
  }, [importedCheckouts]);

  const handledLinkedItems = (productId: string) => {
    setLinkedItemIds((prev) => [...prev, productId]);
  };

  if (shopPopup && shopPopup.type === "MapCheckoutProductsPopup") {
    return (
      <MapCheckoutProductsPopup
        {...shopPopup.props}
        handledLinkedItems={handledLinkedItems}
        onClose={() => setShopPopup(null)}
      />
    );
  }

  return (
    <Popup
      overlayZIndex={BASE_ZINDEX + 100}
      zIndex={BASE_ZINDEX + 200}
      style={{
        maxHeight: 560,
        borderRadius: 10,
      }}
      header={
        <PopupHeader
          style={{
            padding: 0,
            alignItems: "center",
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          <H2
            style={{ margin: "0px", fontSize: 24, fontWeight: 600 }}
            className="title"
          >
            Checkout Errors
          </H2>
          <XIcon
            style={{ cursor: "pointer" }}
            size="large"
            onClick={() => onPopupClose()}
          />
        </PopupHeader>
      }
    >
      <ContentDiv>
        <BodyContainer>
          <div style={{ height: "100%", overflowY: "auto" }} ref={tableRef}>
            <Table>
              <THead
                style={{
                  height: "56px",
                  width: "100%",
                }}
              >
                <TR
                  style={{
                    backgroundColor: `rgb(218, 233, 238)`,
                  }}
                >
                  <TH sticky style={{ width: 24 }}></TH>
                  <TH sticky>Product</TH>
                  <TH sticky>Color</TH>
                  <TH sticky>Size</TH>
                  <TH sticky>
                    {_.startCase(external_store_type?.toLowerCase())} SKU
                  </TH>
                  <TH sticky>Error Type</TH>
                  <TH sticky></TH>
                </TR>
              </THead>
              <TBody>
                {errorItems.map((product) => {
                  const isLinked =
                    product.external_variant_id &&
                    linkedItemIds.length > 0 &&
                    linkedItemIds.includes(
                      product.external_variant_id.toString()
                    );
                  return (
                    <TR
                      key={product.external_variant_id}
                      style={{ paddingLeft: 8, paddingRight: 8 }}
                    >
                      <TD style={{ width: 24 }}>
                        {isLinked ? (
                          <CheckmarkIcon color={colors.green.main} />
                        ) : (
                          <AlertIcon />
                        )}
                      </TD>
                      <TD
                        style={{
                          width: 280,
                          color: `${colors.primary1["70"]}`,
                        }}
                      >
                        {product.product}
                      </TD>
                      <TD style={{ width: 112 }}>{product.color}</TD>
                      <TD style={{ width: 112 }}>{product.size}</TD>
                      <TD style={{ width: 160 }}>{product.sku}</TD>
                      <TD style={{ width: 390 }}>{product.errorType}</TD>
                      <TD style={{ textAlign: "right" }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setShopPopup({
                              type: "MapCheckoutProductsPopup",
                              props: {
                                product,
                                shop_id,
                                order_id,
                                handledLinkedItems,
                                shop_number,
                              },
                            });
                          }}
                          {...(isLinked && { hidden: true })}
                        >
                          Map to Product / Variant
                        </Button>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
          <ScrollContainer
            bottom={-21}
            canScroll={canScrollDown()}
            color={colors.primary1["70"]}
            isUp={false}
            left
            onClick={scrollDown}
            right
            top
            width="100%"
            style={{
              position: "absolute",
              cursor: "pointer",
            }}
            margin={0}
            arrowSize="large"
            padding={10}
          />
        </BodyContainer>
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            marginTop: 24,
            marginBottom: 24,
          }}
        >
          <Button
            onClick={onPopupClose}
            size="large"
            style={{ marginLeft: "auto" }}
          >
            Done
          </Button>
        </div>
      </ContentDiv>
    </Popup>
  );
};

export default CheckoutsErrorPopup;
